'use client';

import { useMutation } from '@tanstack/react-query';
import { type AxiosResponse, isAxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Layout, LayoutWithCarousel } from '@/components/Layout';
import routes from '@/constants/routes';
import { showError } from '@/modules/notification/engine/notification.library';
import { useSignupStore } from '@/store';
import type { BaseResponse, SignupState } from '@/types';

import { signup } from '../../engine/authentication.mutations';
import type { CompanyDetailsFormData } from '../../engine/company-details.model';

import CheckEmailScreen from '../components/CheckEmailScreen';
import CompanyDetailsForm from '../components/CompanyDetailsForm';
import SuccessScreen from '../components/SuccessScreen';

import '../../authentication-screens.styles.css';
import Container from '@/components/container/Container';
import { XpressForBusinessLogo, XpressForBusinessLogoWhite } from '@/components/logos/Logo';


const SignUpStepThree = () => {
	const navigate = useNavigate();
	const { setCompany, user } = useSignupStore((state: SignupState) => state);

	const [showSuccessScreen, setShowSuccessScreen] = useState(false);
	const [showCheckEmailScreen, setShowCheckEmailScreen] = useState(false);

	const { isPending, isSuccess, mutate } = useMutation({
		mutationFn: signup,
		onSuccess: async ({ data }: AxiosResponse<BaseResponse>) => {
			setShowSuccessScreen(true);

			setTimeout(() => {
				setShowCheckEmailScreen(true);
			}, 5000);
		},
		onError: (error) => {
			if (isAxiosError<BaseResponse>(error)) {
				showError(error.message ?? '', {
					summary: 'Error',
				});
			}
		}
	});

	const backButtonHandler = () => {
		navigate(routes['/sign-up/create-credentials']);
	};

	const handleSubmit = (data: CompanyDetailsFormData) => {
		const c = { uuid: uuidv4(), ...data };
		setCompany(c);
		mutate({
			user,
			company: c,
		});
	};

	// This screen layout is quite different from previous two sign up steps
	return (
		<Container className="authentication-parent-container">
			<Container className="flex-container !w-full justify-center">
				<Dialog
					unstyled
					header=""
					visible={showSuccessScreen}
					closable={false}
					maximized={true}
					onHide={() => {
						if (!showSuccessScreen) return;
						setShowSuccessScreen(false);
					}}
					pt={{ root: { className: 'w-full h-full' }, content: { className: 'h-full' } }}
				>
					{showCheckEmailScreen ? (<CheckEmailScreen />) : (<SuccessScreen />)}
				</Dialog>
				<div className='w-full bg-primary h-16 pl-20'>
					<XpressForBusinessLogoWhite className="h-16 mb-6" />
					{/* Note, figma desing also has user icon on the right here. We discussed that with QA. Since the user is not registered at this point we decided that it is desgin error.*/}
				</div>

				<Container className="form-parent-container">
					<Container className="form-container">
						<Card className="sign-up-screen">
							<CompanyDetailsForm
								isLoading={isPending || isSuccess}
								onBack={backButtonHandler}
								onSubmit={handleSubmit}
							/>
						</Card>
					</Container>
				</Container>
			</Container>
		</Container>
	);
};

export default SignUpStepThree;
